import { useApolloClient, useMutation, useQuery } from "@vue/apollo-composable";
import { UpsertPreferencesDocument, UserPreferencesDocument } from "~/graphql/generated/graphql";
import { type NamespaceName, type UserPreferences, getNamespace } from "~/helpers/user-preferences-helpers";
import { sharedBlobWriter } from "~/helpers/prefs-blob-writer";

const BLOB_ORG_PREFIX = "organizationId_";

export const usePreferencesNamespace = <T extends NamespaceName>(namespaceName: T) => {
  const { client } = useApolloClient();

  const query = useQuery(UserPreferencesDocument, {}, { keepPreviousResult: true });

  const mutation = useMutation(UpsertPreferencesDocument);

  const blob = computed(() => query.result.value?.me.preferences?.blob);

  const orgId = computed(() => {
    const orgId = query.result.value?.me.orgId;

    return orgId ? `${BLOB_ORG_PREFIX}${orgId}` : undefined;
  });

  const preferences = computed<Partial<UserPreferences> | undefined>(() => (orgId.value && blob.value?.[orgId.value]) ?? blob.value);

  const queriesLoading = computed(() => query.loading.value || mutation.loading.value);

  const result = ref<UserPreferences[T]>();

  const resultLoading = ref(false);

  const loading = computed(() => queriesLoading.value || resultLoading.value);

  /** NOTE Sven: Unsafe but can only really be fixed by expanding user preferences serverside */
  const update = async (newNamespacePreferences: UserPreferences[T]) => {
    // Guard only on NOT-YET-LOADED / type mismatch — we can't save before we have a baseline. The serialize + fresh
    // read (so no save is dropped or clobbered under load) is the shared writer's job; it's pure + simulation-tested.
    if (!orgId.value || !result.value || newNamespacePreferences.type !== result.value.type) return;
    return sharedBlobWriter(
      { getBlob: () => blob.value as Record<string, unknown> | undefined, getOrg: () => orgId.value, mutate: (b) => mutation.mutate({ input: { blob: b } }) },
      namespaceName,
      newNamespacePreferences,
    );
  };

  watchEffect(async () => {
    if (queriesLoading.value) return;

    resultLoading.value = true;

    try {
      result.value = (await getNamespace(namespaceName, preferences.value ?? {}, client)) as UserPreferences[T];
    } catch (e) {
      console.error(`Failed to load preferences for namespace: ${namespaceName}`, e);
    } finally {
      resultLoading.value = false;
    }
  });

  return {
    result,
    loading,
    update,
    refetch: () => query.refetch(),
  };
};
